import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "ordinateur-ne-demarre-plus",
  silo: "windows",
  isPillar: true,
  h1: "Mon ordinateur ne démarre plus : causes et solutions expliquées simplement",
  metaTitle: "Ordinateur ne démarre plus : que faire ?",
  metaDescription: "Votre PC reste bloqué ou ne s'allume plus ? Suivez notre guide pas à pas pour identifier la cause et redémarrer votre ordinateur rapidement.",
  excerpt: "Vous appuyez sur le bouton d'alimentation et rien ne se passe -- ou votre PC reste bloqué avant même d'arriver au bureau Windows. Ce guide vous aide à identifier la cause et à remettre votre ordinateur en marche.",
  publishedAt: "2024-01-05",
  readingMinutes: 8,
  relatedSlugs: ["pc-bloque-demarrage-windows", "ecran-bleu-bsod", "mise-a-jour-windows-bloquee", "mot-de-passe-windows-oublie"],
  faq: [
    {
      question: "Mon PC s'allume mais l'écran reste noir -- que faire ?",
      answer: "Vérifiez d'abord que l'écran est allumé et correctement branché. Si le PC fait des bips au démarrage, notez leur nombre et leur rythme -- c'est un code d'erreur. Si l'écran reste noir avec le PC allumé (ventilateur qui tourne), le problème vient de la carte graphique ou de la RAM.",
    },
    {
      question: "Le PC ne démarre pas après une coupure de courant -- que faire ?",
      answer: "Débranchez le PC du secteur, attendez 30 secondes, rebranchez. Si ça ne fonctionne pas, le bloc d'alimentation a peut-être été endommagé par la surtension. Un onduleur protège votre matériel des coupures et surtensions.",
    },
    {
      question: "Peut-on récupérer les données d'un PC qui ne démarre plus ?",
      answer: "Dans la grande majorité des cas, oui. Si le problème vient de Windows (logiciel) et non du disque dur lui-même, nous pouvons démarrer depuis un support externe et récupérer vos données avant toute réparation.",
    },
    {
      question: "Un PC portable qui ne démarre plus peut-il être réparé à domicile ?",
      answer: "Oui, dans la plupart des cas. Un technicien se déplace chez vous ou sur votre site pour diagnostiquer et réparer, qu'il s'agisse d'un problème logiciel ou d'une panne matérielle (alimentation, écran, RAM). Le diagnostic est gratuit et un devis vous est remis avant toute intervention.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Un PC qui ne démarre plus est stressant, surtout si vos données importantes sont dessus. Mais avant de paniquer, sachez que la plupart des pannes de démarrage sont d'origine logicielle -- et donc entièrement réparables sans perte de données.",
    },
    {
      type: "h2",
      text: "Diagnostic rapide : qu'entendez-vous et voyez-vous au démarrage ?",
    },
    {
      type: "ul",
      items: [
        "Rien du tout (pas de ventilateur, pas d'écran) → problème d'alimentation",
        "Ventilateur qui tourne mais écran noir → problème de carte graphique, RAM ou écran",
        "Logo Windows qui apparaît puis écran noir → problème de pilote ou de Windows",
        "Message d'erreur texte → problème de disque dur ou de Windows",
        "[Écran bleu](slug:ecran-bleu-bsod) → erreur système critique",
        "[PC bloqué sur 'Préparation de Windows'](slug:pc-bloque-demarrage-windows) → problème de mise à jour",
      ],
    },
    {
      type: "h2",
      text: "Cas 1 : le PC ne s'allume pas du tout",
    },
    {
      type: "p",
      text: "Vérifiez dans l'ordre : câble d'alimentation bien branché, multiprise allumée, prise secteur fonctionnelle (testez avec un autre appareil). Pour un PC fixe, vérifiez que le bouton d'alimentation sur la multiprise est en position ON. Pour un portable, essayez sans la batterie, uniquement avec l'adaptateur secteur.",
    },
    {
      type: "h2",
      text: "Cas 2 : PC allumé mais écran noir",
    },
    {
      type: "p",
      text: "Vérifiez que l'écran est allumé et le câble bien branché. Essayez d'appuyer sur les touches Windows + P pour afficher les options d'affichage. Si vous avez un PC fixe avec une carte graphique dédiée, essayez de brancher l'écran sur la sortie de la carte mère plutôt que sur la carte graphique.",
    },
    {
      type: "h2",
      text: "Cas 3 : Windows refuse de démarrer (message d'erreur)",
    },
    {
      type: "p",
      text: "Les messages comme 'Bootmgr is missing', 'Reboot and select proper boot device' ou 'Operating System not found' indiquent que Windows ne trouve pas son disque de démarrage. Causes possibles : ordre de démarrage incorrect dans le BIOS, disque dur défaillant, ou fichiers de démarrage corrompus.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Ne forcez pas les réparations seul si vous n'êtes pas à l'aise",
      text: "Les manipulations dans le BIOS ou avec les outils de récupération Windows peuvent aggraver la situation si elles sont mal exécutées. Faites appel à un technicien qui pourra intervenir à domicile ou sur site.",
    },
    {
      type: "h2",
      text: "Cas 4 : PC bloqué sur le logo Windows",
    },
    {
      type: "p",
      text: "Si Windows reste bloqué indéfiniment sur son logo de démarrage, tentez de forcer 3 redémarrages successifs (bouton d'alimentation appuyé 5 secondes). Windows déclenchera automatiquement un outil de réparation au prochain démarrage.",
    },
    {
      type: "h2",
      text: "Solution universelle : le mode de récupération Windows",
    },
    {
      type: "p",
      text: "Windows dispose d'un environnement de récupération accessible même quand le système ne démarre pas. Après 3 démarrages forcés, ou en maintenant F8 ou Maj+F8 au démarrage (selon les PC), vous accédez aux options avancées : réparation du démarrage, restauration du système, invite de commandes.",
    },
    {
      type: "h2",
      text: "Quand appeler un technicien",
    },
    {
      type: "p",
      text: "Si vous n'arrivez pas à accéder aux options de récupération, si le disque dur fait des bruits inhabituels (cliquetis), ou si les données à récupérer sont importantes, contactez-nous. Un technicien peut se déplacer chez vous ou sur votre site pour établir un diagnostic et vous orienter vers la meilleure solution.",
    },
  ],
};

export default article;
